import { css } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import { Field } from '@atlaskit/form';
import Select, { ValueType } from '@atlaskit/select';

import { fontSize } from '@/components/signup/utils/font-size';
import { ExistingSite } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { useSiteActivationContext } from '../../hooks/site-activation-context';

export const Dropdown = ({ sites }: { sites: ExistingSite[] }) => {
	const {
		sitePickerValue,
		setSitePickerValue,
		isSubmitting,
		isRequestInProgress,
		isWorkspaceProduct,
	} = useSiteActivationContext();

	return (
		<div data-testid="adg4-site-picker" css={inputAndLabelStyles}>
			<Field<ValueType<ExistingSite>>
				aria-required
				isRequired
				defaultValue={sites[0]}
				isDisabled={isSubmitting}
				id="site-picker-field"
				name="site-picker-field"
				testId="site-picker-field"
				label={
					<span css={labelCss}>
						{isWorkspaceProduct ? (
							<FormattedMessage id="siteActivation.yourWorkspace" defaultMessage="Your workspace" />
						) : (
							<FormattedMessage id="WacSignupForm.siteNameN2ELabel" defaultMessage="Your site" />
						)}
					</span>
				}
			>
				{({ fieldProps: { id, ...rest } }) => (
					<Select
						{...rest}
						inputId={id}
						options={sites}
						getOptionLabel={(option) => option.url.replace('https://', '')}
						getOptionValue={(option) => option.cloudId}
						value={sitePickerValue}
						onChange={(newValue) => setSitePickerValue(newValue ?? sites[0])}
						isLoading={isRequestInProgress}
						isSearchable={false}
						styles={{
							menu: (base) => ({
								...base,
								fontSize: fontSize.md,
							}),
							container: (base) => ({
								...base,
								fontFamily: 'inherit',
							}),
							valueContainer: (base) => ({
								...base,
								fontSize: fontSize.md,
								minHeight: '40px',
								paddingTop: '8px',
								paddingRight: '6px',
								paddingBottom: '8px',
								paddingLeft: '6px',
							}),
							singleValue: (base) => ({
								...base,
								margin: 0,
							}),
						}}
					/>
				)}
			</Field>
		</div>
	);
};

const labelCss = css({
	fontSize: '14px',
	fontWeight: 'normal',
	lineHeight: '20px',
});

const inputAndLabelStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	input: {
		fontSize: fontSize.md,
		paddingInlineStart: '12px !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	label: {
		fontFamily: 'inherit',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	["span[title='required']"]: {
		display: 'none',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['label + div > div:first-of-type, div[data-disabled]']: {
		borderRadius: 6,
		borderWidth: '2px',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['input, label + div > div > div']: {
		paddingInlineStart: '12px !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	["div[role='listbox']"]: {
		padding: '0 !important',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		['> div']: {
			paddingInline: 12,
			paddingBlock: 10,
		},
	},
});
